import type { LandingPageSpaceType, LandingPageStyle } from './SampleImage';

interface AllImages {
  [key: string]: { [key: string]: string }[];
}

export interface SampleImageMeta {
  imgUrl: string;
  spaceType: LandingPageSpaceType;
  style: LandingPageStyle;
}

const importImages = (r: __WebpackModuleApi.RequireContext) =>
  r.keys().reduce((images: Record<string, string>, item: string) => {
    const key = item.replace(/.*[\\/]|\.[^.]+$/g, ''); // strips the path and file extension
    images[key] = r(item) as string;
    return images;
  }, {});

export const SUPPORTED_PRO_PAGE_STYLES: { [key: string]: LandingPageStyle } = {
  contemporary: 'Contemporary',
  coastal: 'Coastal',
  farmhouse: 'Farmhouse',
  midCenturyModern: 'Mid-Century Modern',
  traditional: 'Traditional',
};

export const SUPPORTED_PRO_PAGE_SPACE_TYPES: {
  [key: string]: LandingPageSpaceType;
} = {
  kitchen: 'kitchen',
  bathroom: 'bathroom',
  livingRoom: 'living_room',
  exterior: 'exterior',
};

const bathroom1 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/bathroom/example1',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const bathroom2 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/bathroom/example2',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const bathroom3 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/bathroom/example3',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const bathroom4 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/bathroom/example4',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const kitchen1 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/kitchen/example1',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const kitchen2 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/kitchen/example2',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const kitchen3 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/kitchen/example3',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const kitchen4 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/kitchen/example4',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const livingRoom1 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/living_room/example1',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const livingRoom2 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/living_room/example2',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const livingRoom3 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/living_room/example3',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);
const livingRoom4 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/living_room/example4',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);
const exterior1 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/exterior/example1',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const exterior2 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/exterior/example2',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const exterior3 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/exterior/example3',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const exterior4 = importImages(
  require.context(
    'assets/img/homeownerApp/proPageSamples/exterior/example4',
    true,
    /^.*\.(jpg|webp)$/,
  ),
);

const allImages: AllImages = {
  bathroom: [bathroom1, bathroom2, bathroom3, bathroom4],
  exterior: [exterior1, exterior2, exterior3, exterior4],
  kitchen: [kitchen1, kitchen2, kitchen3, kitchen4],
  living_room: [livingRoom1, livingRoom2, livingRoom3, livingRoom4],
};

// Implements the logic defined here: https://hoverinc.slack.com/archives/C074B5PQ6DQ/p1723659051467189?thread_ts=1722901705.079469&cid=C074B5PQ6DQ
const getImageSlots = (
  spaceTypes?: LandingPageSpaceType[],
): LandingPageSpaceType[] => {
  const { exterior, bathroom, kitchen, livingRoom } =
    SUPPORTED_PRO_PAGE_SPACE_TYPES;
  if (!spaceTypes) return [exterior, exterior, exterior, exterior];

  // We are assuming pros will always have exterior enabled
  const interiorSpaceTypes = spaceTypes.filter(st => st !== exterior);

  switch (interiorSpaceTypes.length) {
    case 0:
      return [exterior, exterior, exterior, exterior];
    case 1:
      return [exterior, exterior, interiorSpaceTypes[0], interiorSpaceTypes[0]];

    case 2:
      if (interiorSpaceTypes.includes('bathroom')) {
        return [
          exterior,
          exterior,
          interiorSpaceTypes.find(st => st !== bathroom)!,
          bathroom,
        ];
      }
      return [
        exterior,
        exterior,
        interiorSpaceTypes.find(st => st !== livingRoom)!,
        livingRoom,
      ];

    default:
      return [exterior, kitchen, livingRoom, bathroom];
  }
};

const groupBySpaceType = (spaceTypes: LandingPageSpaceType[]) =>
  spaceTypes.reduce((groups, st) => {
    if (!groups[st]) {
      groups[st] = [];
    }
    groups[st].push(st);
    return groups;
  }, {} as Record<string, LandingPageSpaceType[]>);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getRandomStyle = (
  random: number,
  styles?: LandingPageStyle[],
): LandingPageStyle =>
  styles?.[Math.floor(random * styles.length)] as LandingPageStyle;

export const getSampleImages = (
  style: LandingPageStyle | LandingPageStyle[],
  random: number,
  spaceTypes?: LandingPageSpaceType[],
) => {
  const groupedSpaceTypes = groupBySpaceType(getImageSlots(spaceTypes));
  const res: SampleImageMeta[] = [];
  for (const [_, v] of Object.entries(groupedSpaceTypes)) {
    v.forEach((st, i) => {
      const imgStyle = Array.isArray(style)
        ? getRandomStyle(random, style)
        : style;
      res.push({
        imgUrl: allImages[st][i][imgStyle.replace(/\W/g, '').toLowerCase()],
        style: imgStyle,
        spaceType: st,
      });
    });
  }
  return res;
};
